/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { getMatchEquationText, getPlayerImageUrl } from "../../../helper/helper";
import type { IMatchDetails, IPlayersTeamSquad } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import type { ICombinedPointsTableResponse, IDivisionSummary, IPointsRow } from "../../leaguesHome/types/home.d";
import { getMatchVenueText } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/matchDetailDisplay";
import { IcShare } from "../../tournamentPage/icons";
import { codeFor, colorFor } from "../../tournamentPage/matchVm";
import {
  countdownTo, fullName, humanize, initials, longDate, matchTime, norm, pad2, resultFor, roleTag, teamInnings, timeLabel, whenLabel,
} from "./helpers";
import type { FormResult } from "./helpers";
import { officialRows, winProbability } from "../live/helpers";
import MatchVideos from "../media/MatchVideos";
import { useMatchMedia } from "../media/useMatchMedia";
import "./upcomingMatch.scss";

type Props = { matchId: string };
type TeamSide = IMatchDetails["teamOne"];
type TabKey = "preview" | "squads" | "info";

const TABS: { key: TabKey; label: string }[] = [
  { key: "preview", label: "Preview" },
  { key: "squads", label: "Squads" },
  { key: "info", label: "Info" },
];

const IC = {
  cal: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3.5" y="5" width="17" height="15.5" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="M3.5 10h17M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>,
  team: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="8.5" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" /><circle cx="16.5" cy="9.5" r="2.6" stroke="currentColor" strokeWidth="1.7" /><path d="M2.8 19c.6-3.4 3-5.2 5.7-5.2s5.1 1.8 5.7 5.2M15 14.3c2.7-.4 5.4.9 6.2 4.7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>,
  table: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3.5" y="4" width="17" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="M3.5 9.5h17M3.5 14.8h17M9 4v16" stroke="currentColor" strokeWidth="1.7" /></svg>,
};

const Empty = ({ icon, title, text }: { icon: ReactNode; title: string; text: string }) => (
  <div className="emp"><span className="ic">{icon}</span><h4>{title}</h4><p>{text}</p></div>
);

const Crest = ({ name, code, image, size }: { name: string; code?: string | null; image?: string | null; size?: number }) => {
  const url = getPlayerImageUrl(image) ?? "";
  const style: React.CSSProperties = { background: colorFor(name) };
  if (size) { style.width = size; style.height = size; style.fontSize = size < 30 ? 8 : 12; }
  const [bad, setBad] = useState(false);
  return <span className="crest" style={style}>{url && !bad ? <img src={url} alt="" onError={() => setBad(true)} /> : codeFor(name, code)}</span>;
};

/** Team logo for the sticky bar (falls back to the team code on a coloured circle). */
const MiniCrest = ({ name, code, image }: { name: string; code?: string | null; image?: string | null }) => {
  const url = getPlayerImageUrl(image) ?? "";
  const [bad, setBad] = useState(false);
  return <span className="c" style={{ background: colorFor(name) }}>{url && !bad ? <img src={url} alt="" onError={() => setBad(true)} /> : codeFor(name, code)}</span>;
};

const Av = ({ name, image, color }: { name: string; image?: string | null; color: string }) => {
  const url = getPlayerImageUrl(image);
  const [bad, setBad] = useState(false);
  return <span className="av" style={{ background: `${color}1A`, color }}>{url && !bad ? <img src={url} alt="" onError={() => setBad(true)} /> : initials(name)}</span>;
};

const useTeamForm = (snapshotId?: string) =>
  useQuery<any[]>({
    queryKey: [QUERY_KEY.matchByTeamAndStatus, snapshotId, "recent"],
    enabled: !!snapshotId,
    queryFn: async () => {
      const [ended, abandoned] = await Promise.all([
        Api.get(QUERY_KEY.matchByTeamAndStatus, { params: { teamId: snapshotId, status: "ENDED", page: 0, size: 5 } }).catch(() => null),
        Api.get(QUERY_KEY.matchByTeamAndStatus, { params: { teamId: snapshotId, status: "ABANDONED", page: 0, size: 5 } }).catch(() => null),
      ]);
      return [...(ended?.data?.matchResponseList ?? []), ...(abandoned?.data?.matchResponseList ?? [])]
        .sort((a: any, b: any) => matchTime(b) - matchTime(a))
        .slice(0, 5);
    },
  });

const useSquad = (matchId: string, snapshotId?: string) =>
  useQuery<IPlayersTeamSquad>({
    queryKey: [QUERY_KEY.getTeamSquadForMatch, matchId, "upcoming", snapshotId],
    enabled: !!snapshotId,
    queryFn: async () => (await Api.get(`${QUERY_KEY.getTeamSquadForMatch}/${matchId}/${snapshotId}`)).data,
  });

const UpcomingMatchPage = ({ matchId }: Props) => {
  const navigate = useNavigate();
  const videos = useMatchMedia(matchId);
  const [tab, setTab] = useState<TabKey>("preview");
  const [now, setNow] = useState(() => Date.now());
  const [stuck, setStuck] = useState(false);
  const teamsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const { data: match, isLoading, isError } = useQuery<IMatchDetails>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId],
    enabled: !!matchId,
    queryFn: async () => (await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}`)).data,
  });

  const one = match?.teamOne, two = match?.teamTwo;
  const formOne = useTeamForm(one?.teamSnapshotId), formTwo = useTeamForm(two?.teamSnapshotId);
  const squadOne = useSquad(matchId, one?.teamSnapshotId), squadTwo = useSquad(matchId, two?.teamSnapshotId);

  const tournamentId: string | undefined = (match?.tournament as any)?.tournamentId || sessionStorage.getItem("tournamentId") || undefined;
  const { data: divisionsPayload } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    enabled: !!tournamentId,
    queryFn: async () => (await Api.get(`${QUERY_KEY.getDivisions}/${tournamentId}`)).data,
  });
  const divisionId = useMemo(() => {
    const p: any = divisionsPayload;
    if (!p) return undefined;
    const list: IDivisionSummary[] = Array.isArray(p) ? p : (p.divisionResponseList ?? p.divisionResponses ?? []);
    return list[0]?.divisionId;
  }, [divisionsPayload]);
  const { data: tableData } = useQuery<ICombinedPointsTableResponse>({
    queryKey: [QUERY_KEY.getCombinedPointsTableByDivision, divisionId],
    enabled: !!divisionId,
    queryFn: async () => (await Api.get(`${QUERY_KEY.getCombinedPointsTableByDivision}/${divisionId}`)).data,
  });

  useEffect(() => {
    const el = teamsRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { rootMargin: "-120px 0px 0px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [match]);

  const start = match?.startDate ? new Date(match.startDate) : null;
  const startValid = !!start && !Number.isNaN(start.getTime());
  const cd = startValid ? countdownTo(start.getTime(), now) : null;
  const nowDate = new Date(now);
  const when = startValid ? whenLabel(start, nowDate) : "";
  const tName = match?.tournament?.tournamentName ?? "";
  const ground = getMatchVenueText(match);
  const groundName = match?.groundId?.groundName || match?.teamOne?.groundId?.groundName || "";
  const toss = (match?.tossResult ?? "").trim();
  const squadsAnnounced = (squadOne.data?.playingSquad?.length ?? 0) > 0 && (squadTwo.data?.playingSquad?.length ?? 0) > 0;

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${one?.teamName} vs ${two?.teamName}`, url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success("Match link copied"); }
    } catch { /* cancelled */ }
  };
  const openPlayer = (id?: string) => { if (id) navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(id)}`); };

  const pointsRows: IPointsRow[] = useMemo(() => {
    if (!tableData || !one || !two) return [];
    const groups = Object.values(tableData).filter((g) => Array.isArray(g) && g.length);
    const hit = (r: IPointsRow) => [one.teamName, two.teamName].some((n) => norm(r.teamSnapshotName) === norm(n));
    const group = groups.find((g) => g.some(hit)) ?? groups[0] ?? [];
    return [...group].sort((a, b) => a.rank - b.rank);
  }, [tableData, one, two]);

  // The points-table API carries no logos, so look them up from the tournament's teams (division > group > teams),
  // by team snapshot id first and team name as the fallback; the two teams of this match are always covered.
  const teamLogos = useMemo(() => {
    const byId = new Map<string, string>();
    const byName = new Map<string, string>();
    const add = (t: any) => {
      const img = t?.imageUrl ?? t?.teamLogo ?? t?.team?.imageUrl ?? null;
      if (!img) return;
      if (t.teamSnapshotId) byId.set(String(t.teamSnapshotId), img);
      const nm = t.teamName ?? t.team?.teamName;
      if (nm) byName.set(norm(nm), img);
    };
    const visit = (d: any) => {
      (d?.teams ?? []).forEach(add);
      (d?.childDivisions ?? []).forEach(visit);
    };
    const p: any = divisionsPayload;
    (Array.isArray(p) ? p : (p?.divisionResponseList ?? p?.divisionResponses ?? [])).forEach(visit);
    [one, two].forEach((t) => t && add({ teamName: t.teamName, teamSnapshotId: t.teamSnapshotId, imageUrl: t.imageUrl }));
    return { byId, byName };
  }, [divisionsPayload, one, two]);
  const logoFor = (r: IPointsRow) =>
    r.teamLogoUrl ?? ((r as { teamSnapshotId?: string }).teamSnapshotId ? teamLogos.byId.get(String((r as { teamSnapshotId?: string }).teamSnapshotId)) : undefined) ?? teamLogos.byName.get(norm(r.teamSnapshotName));

  if (isLoading) return <div className="mu"><div className="mu-state">Loading match…</div></div>;
  if (isError || !match || !one || !two) return <div className="mu"><div className="mu-state">We couldn’t load this match. Please try again later.</div></div>;

  const metaParts = [humanize(match.matchType), groundName || (ground !== "-" ? ground : "")].filter(Boolean);

  const chips = (results: FormResult[]) =>
    results.length
      ? <><span className="lb">FORM</span>{results.map((r, i) => <span key={i} className={`fm ${r}`}>{r}</span>)}</>
      : <span className="none">No completed matches yet</span>;
  const resultsOf = (list: any[] | undefined, team: TeamSide) => (list ?? []).map((m) => resultFor(m, team.teamName));

  /* form */
  const formCol = (team: TeamSide, list: any[] | undefined) => {
    const results = resultsOf(list, team);
    const w = results.filter((r) => r === "W").length, l = results.filter((r) => r === "L").length;
    return (
      <div key={team.teamSnapshotId}>
        <div className="fhd"><Crest name={team.teamName} code={team.teamCode} image={team.imageUrl} size={44} />{team.teamName}<span className="rec">{results.length ? `${w} won · ${l} lost` : ""}</span></div>
        {list && list.length ? (
          <ul className="frm">
            {list.map((m, i) => {
              const o = results[i];
              const opp = norm(m.teamOne?.teamName) === norm(team.teamName) ? m.teamTwo?.teamName : m.teamOne?.teamName;
              const sc = teamInnings(m, team.teamName);
              const d = matchTime(m) ? new Date(matchTime(m)).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
              const eq = getMatchEquationText({ ...m, matchStatus: m.matchStatus || "ENDED" });
              return (
                <li key={m.matchId ?? i}>
                  <span className={`fm ${o}`}>{o}</span>
                  <span><b>v {opp ?? "-"}</b><span className="m">{[d, eq !== "-" ? eq : ""].filter(Boolean).join(" · ")}</span></span>
                  <span className="sc2">{sc ? sc.text : "DNB"}<small>{sc ? `${sc.overs} ov` : ""}</small></span>
                </li>
              );
            })}
          </ul>
        ) : (
          <Empty icon={IC.cal} title="No matches yet" text={`${team.teamName} have no completed matches to show yet.`} />
        )}
      </div>
    );
  };
  const formLoading = (!formOne.data && formOne.isLoading) || (!formTwo.data && formTwo.isLoading);

  /* players to watch */
  const sideOf = (x: { teamId?: string; teamName?: string }, team: TeamSide) => x.teamId === team.teamId || norm(x.teamName) === norm(team.teamName);
  const watch = (team: TeamSide) => {
    const rows: { id: string; name: string; img?: string | null; role: string; v: number; unit: string; extra: string }[] = [];
    const bat = (match.topBatters ?? []).find((b) => sideOf(b, team));
    const bowl = (match.topBowlers ?? []).find((b) => sideOf(b, team));
    if (bat) rows.push({ id: bat.player?.playerId, name: fullName(bat.player) || "-", img: bat.player?.image || bat.player?.imageUrl, role: "Batter", v: bat.runs, unit: "runs", extra: `SR ${(bat.strikeRate ?? 0).toFixed(1)}` });
    if (bowl) rows.push({ id: bowl.player?.playerId, name: fullName(bowl.player) || "-", img: bowl.player?.image || bowl.player?.imageUrl, role: "Bowler", v: bowl.wickets, unit: "wickets", extra: `Econ ${(bowl.economy ?? 0).toFixed(2)}` });
    return rows;
  };
  const watchOne = watch(one), watchTwo = watch(two);
  const hasWatch = watchOne.length + watchTwo.length > 0;
  const watchCol = (team: TeamSide, rows: ReturnType<typeof watch>) => (
    <div>
      {rows.map((p) => (
        <div className="pw" key={`${p.id}-${p.role}`}>
          <Av name={p.name} image={p.img} color={colorFor(team.teamName)} />
          <button type="button" className="plink" onClick={() => openPlayer(p.id)}><b>{p.name}</b><span className="ro">{team.teamName} · {p.role}</span></button>
          <span className="v">{p.v}<small>{p.unit} · {p.extra}</small></span>
        </div>
      ))}
    </div>
  );

  /* squads */
  const squadBox = (team: TeamSide, sq?: IPlayersTeamSquad) => (
    <div className="box" key={team.teamSnapshotId}>
      <div className="box-h"><Crest name={team.teamName} code={team.teamCode} image={team.imageUrl} size={44} /><h3>{team.teamName}</h3><span className="sub">{sq?.playingSquad?.length ? "Playing XI · announced" : "Squad"}</span></div>
      <ul className="xi">
        {(sq?.playingSquad ?? []).map((p, i) => {
          const nm = fullName(p) || "-", tag = roleTag(p.playerRole);
          return (
            <li key={p.playerId}>
              <span className="no">{i + 1}</span>
              <Av name={nm} image={p.imageUri} color={colorFor(team.teamName)} />
              <button type="button" className="plink" onClick={() => openPlayer(p.playerId)}><b>{nm}{tag ? <span className="tag">{tag}</span> : null}</b><span className="ro">{[p.battingStyle, p.bowlingStyle].filter(Boolean).map((s) => humanize(s)).join(" · ")}</span></button>
              <span />
            </li>
          );
        })}
      </ul>
      {(sq?.bench?.length ?? 0) > 0 && (
        <div className="dnb"><div className="bench"><b>Bench</b>{sq?.bench.map((p) => <span key={p.playerId}>{fullName(p)}</span>)}</div></div>
      )}
    </div>
  );
  const squadLoading = (!squadOne.data && squadOne.isLoading) || (!squadTwo.data && squadTwo.isLoading);

  /* info */
  const infoRows: [string, string][] = ([
    ["Format", humanize(match.matchType)],
    ["Date", startValid ? longDate(start) : ""],
    ["Start", startValid ? timeLabel(start) : ""],
    ["Toss", toss],
    ["Ground", ground !== "-" ? ground : ""],
    ["League", match.leagueName ?? ""],
    ["Tournament", tName],
    ["Pitch", humanize(match.pitchType)],
  ] as [string, string][]).filter(([, v]) => v);
  const offRows = officialRows(match);
  // pre-toss / post-toss prediction, only when the league shows it (same flag as the live page)
  const wp = winProbability(match);
  const wpReasons: string[] = ((match as { winProbabilityReason?: string[] | null }).winProbabilityReason ?? []).filter((r) => r && r.trim());
  const wpStage = (match as { winPredictionStage?: string | null }).winPredictionStage === "post_toss" ? "After the toss" : "Before the toss";

  const facts: [string, ReactNode][] = [];
  if (startValid) facts.push(["Starts", <>{when}, {timeLabel(start)}</>]);
  if (toss) facts.push(["Toss", <>{toss}</>]);
  if (ground !== "-") facts.push(["Ground", <>{groundName || ground}{match.city ? <> <small>{match.city}</small></> : null}</>]);
  facts.push(["Squads", squadsAnnounced ? <>Announced <small>· both XIs</small></> : <small>Not announced yet</small>]);

  return (
    <div className="mu">
      <section className="hero">
        <div className="hbg" aria-hidden="true"><span className="halo" /><span className="tile" /><span className="scrim-l" /><span className="scrim-b" /><span className="grain" /></div>
        <div className="wrap">
          <nav className="crumb" aria-label="Breadcrumb">
            {tournamentId ? <Link to={`/tournamentDetails/${tournamentId}`}>{tName || "Tournament"}</Link> : <span>{tName || "Tournament"}</span>}
            <span>/</span>
            {tournamentId ? <Link to={`/tournamentDetails/${tournamentId}`}>Matches</Link> : <span>Matches</span>}
            <span>/</span>
            <b>{one.teamName} vs {two.teamName}</b>
          </nav>
          <div className="hgrid">
            <div>
              <div className="meta">
                <span className="badge">UPCOMING</span>
                {startValid && <span>{start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })} · {timeLabel(start)}</span>}
                {metaParts.map((p, i) => <span key={i} style={{ display: "contents" }}><i />{p}</span>)}
              </div>
              {tName && <h1>{tName}</h1>}
              <div className="teams" ref={teamsRef}>
                {([[one, formOne.data], [two, formTwo.data]] as [TeamSide, any[] | undefined][]).map(([t, list]) => (
                  <div className="trow" key={t.teamSnapshotId}>
                    <Crest name={t.teamName} code={t.teamCode} image={t.imageUrl} />
                    <div className="nm"><span>{t.teamName}</span></div>
                    <div className="form">{chips(resultsOf(list, t))}</div>
                  </div>
                ))}
              </div>
              {startValid && (
                <div className="result">
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style={{ color: "#C9C8E6" }}><rect x="2" y="3" width="12" height="11" rx="2.2" stroke="currentColor" strokeWidth="1.5" /><path d="M2 6.6h12M5.2 1.8v2.6M10.8 1.8v2.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                  {when}, {timeLabel(start)}{ground !== "-" && <span>{groundName || ground}</span>}
                </div>
              )}
            </div>
            <div className="hside">
              <div className="hacts"><button type="button" className="hbtn" onClick={share}><IcShare />Share</button></div>
              {cd && (
                <div className="cdbox">
                  {cd.started ? (
                    <div className="lbl">STARTING SOON</div>
                  ) : (
                    <>
                      <div className="lbl">STARTS IN</div>
                      <div className="cd" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                        <div><b>{pad2(cd.d)}</b><span>DAYS</span></div>
                        <div><b>{pad2(cd.h)}</b><span>HRS</span></div>
                        <div><b>{pad2(cd.m)}</b><span>MINS</span></div>
                      </div>
                    </>
                  )}
                  <div className="st"><span className={squadsAnnounced ? "ok" : ""}>{squadsAnnounced ? "✓ Squads announced" : "Squads not announced yet"}</span></div>
                </div>
              )}
            </div>
          </div>
          <div className="facts" style={{ gridTemplateColumns: `repeat(${facts.length},1fr)` }}>
            {facts.map(([k, v]) => <div key={k}><div className="k">{k}</div><div className="v">{v}</div></div>)}
          </div>
        </div>
      </section>

      <div className="tabsbar">
        <div className="wrap">
          <div className={`mini ${stuck ? "on" : ""}`} aria-hidden={!stuck}>
            <span className="t"><MiniCrest name={one.teamName} code={one.teamCode} image={one.imageUrl} />{codeFor(one.teamName, one.teamCode)}</span>
            <span style={{ color: "var(--faint)" }}>v</span>
            <span className="t"><MiniCrest name={two.teamName} code={two.teamCode} image={two.imageUrl} />{codeFor(two.teamName, two.teamCode)}</span>
            {startValid && <span className="t l">{when}, {timeLabel(start)}</span>}
          </div>
          <div className="tabs" role="tablist" aria-label="Match sections">
            {TABS.map((t) => (
              <button key={t.key} type="button" className="tab" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="wrap main">
        {tab === "preview" && (
          <section className="pane on" role="tabpanel">
            <div className="cols">
              <div className="col">
                <MatchVideos videos={videos} live={false} ended={false} />
                {wp && (
                  <div className="box">
                    <div className="box-h"><h3>Win prediction</h3><span className="sub">{wpStage}</span></div>
                    <div className="wpx">
                      <div className="wpx-t">
                        <span><Crest name={one.teamName} code={one.teamCode} image={one.imageUrl} size={32} /><span className="n">{one.teamName}</span><b>{wp[0]}%</b></span>
                        <span><b>{wp[1]}%</b><span className="n">{two.teamName}</span><Crest name={two.teamName} code={two.teamCode} image={two.imageUrl} size={32} /></span>
                      </div>
                      <div className="wpx-bar" role="img" aria-label={`${one.teamName} ${wp[0]}%, ${two.teamName} ${wp[1]}%`}><i style={{ width: `${Math.min(100, Math.max(0, wp[0]))}%` }} /><i /></div>
                      {wpReasons.length > 0 && <ul className="wpx-r">{wpReasons.map((r) => <li key={r}>{r}</li>)}</ul>}
                    </div>
                  </div>
                )}
                <div className="box">
                  <div className="box-h"><h3>Recent form</h3><span className="sub">Last 5 matches · latest first</span></div>
                  {formLoading ? <div className="empty">Loading…</div> : <div className="ftwo">{formCol(one, formOne.data)}{formCol(two, formTwo.data)}</div>}
                </div>
                {hasWatch && (
                  <div className="box">
                    <div className="box-h"><h3>Players to watch</h3><span className="sub">Recent performance</span></div>
                    <div className="ptw">{watchCol(one, watchOne)}{watchCol(two, watchTwo)}</div>
                  </div>
                )}
              </div>
              <aside className="col rail">
                {pointsRows.length > 0 && (
                  <div className="box">
                    <div className="box-h"><h3>Points table</h3></div>
                    <div className="ptg hd"><span /><span>Team</span><span>P</span><span>W</span><span>L</span><span>Pts</span></div>
                    {pointsRows.map((r, i) => {
                      const hl = [one.teamName, two.teamName].some((n) => norm(r.teamSnapshotName) === norm(n));
                      return (
                        <div className={`ptg ${hl ? "hl" : ""}`} key={r.id ?? i}>
                          <span className="i">{r.rank || i + 1}</span>
                          <span className="tn"><Crest name={r.teamSnapshotName} image={logoFor(r)} size={38} /><span>{r.teamSnapshotName}</span></span>
                          <span>{r.matches}</span><span>{r.won}</span><span>{r.lost}</span><b>{r.points}</b>
                        </div>
                      );
                    })}
                  </div>
                )}
              </aside>
            </div>
          </section>
        )}

        {tab === "squads" && (
          <section className="pane on" role="tabpanel">
            {squadLoading ? <div className="box"><div className="empty">Loading squads…</div></div>
              : (squadOne.data?.playingSquad?.length || squadTwo.data?.playingSquad?.length)
                ? <div className="pteams">{squadBox(one, squadOne.data)}{squadBox(two, squadTwo.data)}</div>
                : <div className="box"><Empty icon={IC.team} title="Squads not announced yet" text="This page updates as soon as both playing XIs are announced." /></div>}
          </section>
        )}

        {tab === "info" && (
          <section className="pane on" role="tabpanel">
            <div className="two">
              <div className="box"><div className="box-h"><h3>Match</h3></div>
                {infoRows.length ? <dl className="dl">{infoRows.map(([k, v]) => <div key={k} style={{ display: "contents" }}><dt>{k}</dt><dd>{v}</dd></div>)}</dl> : <Empty icon={IC.table} title="No details yet" text="Match details will appear here." />}
              </div>
              <div className="box"><div className="box-h"><h3>Officials</h3></div>
                {offRows.length ? <dl className="dl">{offRows.map(([k, v]) => <div key={k} style={{ display: "contents" }}><dt>{k}</dt><dd>{v}</dd></div>)}</dl> : <Empty icon={IC.team} title="To be confirmed" text="Officials have not been assigned yet." />}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default UpcomingMatchPage;
